import axios from 'axios'
import { AuthMigrationError, requestError, resolveMigrationIdentity, requireFreshLogin, synchronizeGoogleId } from './googleMigration'
import { resolveGoogleIdentity, lookupGoogleAccount, verifyGoogleAccount, prepareGoogleRegistration, createGoogleAccount } from './googleRegistration'

const REQUEST_TIMEOUT = 15000

export function createArcClient (apiKey) {
  return axios.create({
    baseURL: process.env.VUE_APP_ARC_SCORE_API,
    timeout: REQUEST_TIMEOUT,
    headers: {
      Authorization: `Basic ${btoa(apiKey + ':')}`,
      'Content-Type': 'application/json'
    }
  })
}

export async function getOutreachToken (graphId) {
  if (process.env.NODE_ENV === 'everservice') return null
  try {
    const { data } = await axios({
      method: 'POST',
      url: 'https://sales-outreach.1p21.io/api/token_auth/',
      timeout: REQUEST_TIMEOUT,
      data: { username: graphId, password: process.env.VUE_APP_ARC_SCORE_API_KEY },
      headers: { 'Content-Type': 'application/json' }
    })
    return data.token || null
  } catch (error) {
    // Axios errors contain Authorization headers and must not be logged.
    console.warn('Outreach authentication is temporarily unavailable.')
    return null
  }
}

async function getManagementClient () {
  // Reuses the application's existing Management API configuration.
  const { data: token } = await axios({
    method: 'POST',
    url: `https://${process.env.VUE_APP_AUTH0_DOMAIN}/oauth/token`,
    timeout: REQUEST_TIMEOUT,
    data: new URLSearchParams({
      client_id: process.env.VUE_APP_AUTH0_MANAGEMENT_API_SERVICE_CLIENT_ID,
      client_secret: process.env.VUE_APP_AUTH0_MANAGEMENT_API_SERVICE_CLIENT_SECRET,
      audience: process.env.VUE_APP_AUTH0_MANAGEMENT_API_SERVICE_AUDIENCE,
      grant_type: process.env.VUE_APP_AUTH0_MANAGEMENT_API_SERVICE_GRANT_TYPE
    }),
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
  })
  if (!token.access_token) throw new Error('Management token unavailable')
  return axios.create({
    baseURL: process.env.VUE_APP_AUTH0_MANAGEMENT_API_SERVICE_AUDIENCE,
    timeout: REQUEST_TIMEOUT,
    headers: { Authorization: `Bearer ${token.access_token}` }
  })
}

async function registrationOptions (context, assertCurrent, managementClient, managementUser) {
  assertCurrent()
  if (!managementClient) {
    managementClient = await getManagementClient()
    assertCurrent()
    const { data } = await managementClient.get(`users/${encodeURIComponent(context.user.sub)}`)
    managementUser = data
  }
  assertCurrent()
  const identity = resolveGoogleIdentity({ ...context, managementUser })
  return {
    managementClient,
    identity,
    context,
    assertCurrent,
    lookupClient: createArcClient(process.env.VUE_APP_ARC_SCORE_API_KEY),
    createUserClient: createArcClient,
    baselineState: context.accountCheck || (context.accountCheck = {})
  }
}

export async function prepareRegistration (context, assertCurrent) {
  try {
    return await prepareGoogleRegistration(await registrationOptions(context, assertCurrent))
  } catch (error) { throw requestError(error) }
}

export async function submitRegistration (context, assertCurrent, details) {
  try {
    return await createGoogleAccount(await registrationOptions(context, assertCurrent), details)
  } catch (error) { throw requestError(error) }
}

// Cookie publication and concurrent-login coordination belong to initializeAuth.
export async function tokenSwap (context, assertCurrent) {
  try {
    assertCurrent()
    const managementClient = await getManagementClient()
    assertCurrent()
    const { data: managementUser } = await managementClient.get(`users/${encodeURIComponent(context.user.sub)}`)
    assertCurrent()
    if (context.user.sub.startsWith('google-oauth2|')) {
      const options = await registrationOptions(context, assertCurrent, managementClient, managementUser)
      const account = await lookupGoogleAccount(options)
      if (account) return await verifyGoogleAccount({ ...options, account })
      throw new AuthMigrationError('REGISTRATION_REQUIRED', 'Finish setting up your ARC account.', { action: 'register' })
    }
    const identity = resolveMigrationIdentity({ ...context, managementUser })
    return await synchronizeGoogleId({
      lookupClient: createArcClient(process.env.VUE_APP_ARC_SCORE_API_KEY),
      createUserClient: createArcClient,
      identity,
      baselineState: context.accountCheck,
      assertFresh: () => requireFreshLogin(context, identity.googleId),
      assertCurrent
    })
  } catch (error) {
    throw requestError(error)
  }
}
